import React, { useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import html2canvas from 'html2canvas';
import SimpleInfoDialog from '../../components/dialog/SimpleInfoDialog';
import HelpOutlineIcon from '@material-ui/icons/HelpOutline';
import { Button, Typography, Checkbox, FormControlLabel, TextField, IconButton, Paper } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { fromEpoch } from '../../../common/utils';
import EvolutionChart from './EvolutionChart';
import { INTERNALS, ESTEVES_LABELS } from '../../../common/metadata';

/**
 * @function useStyles
 * @description Hook for styling using Material-UI's makeStyles. Defines CSS styles for the
 * transect evolution chart, statistics panel, and transect selection list.
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @memberof module:SHARED
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.default,
    color: theme.palette.text.primary,
    padding: theme.spacing(1.5),
    borderRadius: 10,
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1.5),
    paddingBottom: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  mainPaper: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(2.5),
    flexWrap: 'wrap',
    padding: theme.spacing(2),
    borderRadius: 10,
  },
  chartWrapper: {
    flex: 1,
    minWidth: 320,
  },
  statsPanel: {
    minWidth: 240,
  },
  statsTitle: {
    fontWeight: 700,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  statRow: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: theme.spacing(2),
    padding: theme.spacing(0.5, 0),
    borderBottom: `1px solid ${theme.palette.divider}`,
    '&:last-child': {
      borderBottom: 'none',
    },
  },
  statLabel: {
    color: theme.palette.text.secondary,
  },
  statValue: {
    fontWeight: 600,
    textAlign: 'right',
  },
  controlsPaper: {
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    padding: theme.spacing(1.25, 2),
    marginTop: theme.spacing(2),
    borderRadius: 10,
  },
  transectSection: {
    marginTop: theme.spacing(2),
  },
  transectListTitle: {
    fontWeight: 700,
    fontSize: '0.75rem',
    textTransform: 'uppercase',
    letterSpacing: '0.5px',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
  transectList: {
    maxHeight: 220,
    overflowY: 'auto',
    display: 'flex',
    flexWrap: 'wrap',
    padding: theme.spacing(1, 2),
    borderRadius: 10,
  },
  transectItem: {
    flex: '0 0 auto',
    minWidth: 220,
  },
}));

/**
 * @function generateRegression
 * @description Computes the fitted regression y-value for each observation, using the given
 * slope/intercept, so it can be plotted as the trend line alongside the raw data.
 * @param {Number} slope - Regression line slope.
 * @param {Number} intercept - Regression line intercept.
 * @param {Array<Array<Number>>} x - Observations as [x, y] pairs; only the x (first) value is used.
 * **Called by:
 * @see {TransectEvolution}
 * @memberof module:SHARED
 * @returns {Array<Number>} Fitted y-value for each observation.
 */
const generateRegression = (slope, intercept, x) => {
  return x.map((values) => values[0] * slope + intercept);
};

/**
 * @function fromEpochDays
 * @description Converts a (possibly fractional) number of days since EPOCH — as computed
 * server-side by `statistics.js`'s regression (`ee.Date#difference(..., 'day')`, an exact
 * fractional value derived from the image's real acquisition time) — to a UTC moment, preserving
 * the time-of-day. Deliberately does not use `fromEpoch(days, 'days')` directly: moment's `add`
 * rounds a fractional 'days' amount to the nearest whole day instead of keeping the fraction,
 * which silently shifted the displayed date by up to a day (rounding 15/01 13:29 up to 16/01,
 * for example) whenever the day count wasn't a whole number. Converting to milliseconds first —
 * a fixed-length, non-ambiguous unit — avoids that rounding.
 * @param {Number} days - Days since EPOCH.
 * **Called by:
 * @see {TransectEvolution} - to format each transect list label and the chart's x-axis dates.
 * @memberof module:SHARED
 * @returns {moment.Moment} UTC moment at the exact date/time.
 */
const fromEpochDays = (days) => fromEpoch(days * 86400000, 'milliseconds').utc();

/**
 * @function formatted
 * @description Formats a numeric value with a fixed number of decimal places (fewer when the
 * magnitude is large) and an optional trailing unit label. Falls back to 1 when value is NaN.
 * @param {Number} value - Value to format.
 * @param {String} [units=''] - Unit label appended after the number.
 * @param {Number} [places=3] - Decimal places to use when |value| < 100.
 * **Called by:
 * @see {TransectEvolution}
 * @memberof module:SHARED
 * @returns {String} Formatted value with its unit.
 */
  const formatted = (value, units = '', places = 3) => {
    if (units.length > 0) units = ' ' + units;
    if (value === null || value === undefined || isNaN(value) || !isFinite(value)) {
      return 'N/A' + units;
    }
    const safeValue = value;
    const decimals = Math.abs(safeValue) >= 100 ? 2 : places;
    return safeValue.toFixed(decimals) + units;
  };

/**
 * @function getOutlierIndices
 * @description Identifies observations whose residual from the given regression line exceeds
 * `multiplier` standard deviations, so they can be excluded from the displayed statistics.
 * @param {Array<Array<Number>>} values - Observations as [x, y] pairs.
 * @param {Number} slope - Regression line slope.
 * @param {Number} intercept - Regression line intercept.
 * @param {Number} [multiplier=1] - Number of standard deviations used as the outlier threshold.
 * **Called by:
 * @see {TransectEvolution}
 * @memberof module:SHARED
 * @returns {Array<Number>} Indices (into `values`) of the detected outliers.
 */
const getOutlierIndices = (values, slope, intercept, multiplier = 1) => {
  let sumR = 0;
  const residuals = values.map(([x, y]) => {
    const r = y - (slope * x + intercept);
    sumR += r;
    return r;
  });
  const mean = sumR / residuals.length;
  const variance = residuals.reduce((sum, r) => sum + (r - mean) ** 2, 0) / residuals.length;
  const threshold = multiplier * Math.sqrt(variance);
  return residuals.reduce((acc, r, i) => {
    if (Math.abs(r) > threshold) acc.push(i);
    return acc;
  }, []);
};

/**
 * @function computeStats
 * @description Fits a linear regression to the given [x, y] observations and derives the
 * shoreline change statistics from it.
 * 1. Computes the least-squares slope and intercept.
 * 2. Computes the residuals and the coefficient of determination (r).
 * 3. Derives SCE (sum of squared residuals), NSM (root-mean-square residual), EPR (slope scaled
 *    to meters/month) and LRR (slope scaled to meters/year).
 * 4. Derives the standard error of the estimate (lse) and a 95%-style confidence interval (lci),
 *    plus the EPR uncertainty (eprUnc).
 * @param {Array<Array<Number>>} values - Observations as [x, y] pairs.
 * **Called by:
 * @see {TransectEvolution}
 * @memberof module:SHARED
 * @returns {Object} Statistics object with slope, intercept, r, sce, nsm, epr, lrr, lse, lci, eprUnc.
 */
const computeStats = (values) => {
  if (values.length === 0) return {};
  const n = values.length;
  const x = values.map(([x]) => x);
  const y = values.map(([_, y]) => y);
  const xMean = x.reduce((a, b) => a + b, 0) / n;
  const yMean = y.reduce((a, b) => a + b, 0) / n;
  const numerator = x.reduce((sum, xi, i) => sum + (xi - xMean) * (y[i] - yMean), 0);
  const denominator = x.reduce((sum, xi) => sum + Math.pow(xi - xMean, 2), 0);
  const slope = denominator === 0 ? 0 : numerator / denominator;
  const intercept = yMean - slope * xMean;
  const residuals = y.map((yi, i) => yi - (slope * x[i] + intercept));
  const rNumerator = residuals.reduce((sum, r) => sum + Math.pow(r, 2), 0);
  const rDenominator = y.reduce((sum, yi) => sum + Math.pow(yi - yMean, 2), 0);
  const r = rDenominator === 0 ? 1 : 1 - rNumerator / rDenominator;
  const sce = rNumerator;
  const nsm = Math.sqrt(sce / n);
  const epr = slope * 30.4375;
  const lrr = slope * 365.25;
  let lse;
  let lci;
  let eprUnc;
  if (n < 3) {
    lse = NaN;
    lci = NaN;
    eprUnc = NaN;
  } else {
    lse = Math.sqrt(rNumerator / (n - 2));
    const tValue = 2.0;
    lci = tValue * lse;
    eprUnc = lse * 30.4375;
  }
  return { slope, intercept, r, sce, nsm, epr, lrr, lse, lci, eprUnc };
};

/**
 * @function TransectEvolution
 * @description React component that displays a single transect's shoreline position evolution:
 * a chart with the raw positions and fitted trend line, a statistics panel (slope, intercept,
 * LRR, SCE, NSM, EPR, etc.), an outlier removal control, chart export, and a checklist to
 * include/exclude individual observations.
 * @param {Object} props - Component properties.
 * @param {Object} props.data - Transect data, including its regression observations under `INTERNALS`.
 * @param {Object} [props.minmaxResults] - Global min/max Y-axis values shared across transects' charts.
 * @see {@link EvolutionChart} - renders the chart itself.
 * @see {@link computeStats}
 * @see {@link getOutlierIndices}
 * @see {@link generateRegression}
 * **Called by:
 * @see {FeatureInfo} - when the clicked map feature is a transect.
 * @memberof module:SHARED
 * @returns {JSX.Element} The transect evolution chart, statistics, and controls.
 */
const TransectEvolution = ({ data, minmaxResults }) => {
  const [t] = useTranslation();
  const classes = useStyles();
  const exportRef = useRef(null);
  const sortedValues = data[INTERNALS].regression.sort();
  const chartLang = t('forms.transectEvolution.chartLanguage');
  const charLanguage = chartLang.toLowerCase().includes('portugu');
  const [selectedIndices, setSelectedIndices] = useState(sortedValues.map((_, index) => index));
  const [removeOutliers, setRemoveOutliers] = useState(false);
  const [outlierMultiplier, setOutlierMultiplier] = useState(1);
  const [showHelpDialog, setShowHelpDialog] = useState(false);

  /**
   * @function handleCheckboxChange
   * @description Toggles whether the observation at the given index is included in the
   * displayed statistics and chart.
   * @param {Number} index - Index of the observation in `sortedValues`.
   * **Called by:
   * @see {TransectEvolution} - via each transect checkbox's onChange.
   * @memberof module:SHARED
   * @returns {void}
   */
  const handleCheckboxChange = (index) => {
    setSelectedIndices((prev) => (prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]));
  };

  /**
   * @function formatDate
   * @description Formats an epoch (in days) as a locale-aware date string.
   * @param {Number} epoch - Number of days since the epoch.
   * **Called by:
   * @see {TransectEvolution} - to label each transect checkbox.
   * @memberof module:SHARED
   * @returns {String} Formatted date ("DD/MM/YYYY" for pt-BR, "MM/DD/YYYY" for en).
   */
  const formatDate = (epoch) => {
    const date = fromEpochDays(epoch);
    return charLanguage ? date.format('DD/MM/YYYY') : date.format('MM/DD/YYYY');
  };

  /**
   * @function handleExportImage
   * @description Renders the chart and statistics panel to an image and triggers its download.
   * **Called by:
   * @see {TransectEvolution} - via the "Export chart" button's onClick.
   * @memberof module:SHARED
   * @returns {Promise<void>}
   */
  const handleExportImage = async () => {
    if (!exportRef.current) return;
    const canvas = await html2canvas(exportRef.current);
    const link = document.createElement('a');
    link.download = 'grafico-completo.png';
    link.href = canvas.toDataURL('image/png');
    link.click();
  };

  const outlierIndices =
    removeOutliers && data?.slope !== undefined && data?.intercept !== undefined
      ? getOutlierIndices(sortedValues, data.slope, data.intercept, outlierMultiplier)
      : [];

  const filteredValues = sortedValues.filter((_, i) => selectedIndices.includes(i) && !outlierIndices.includes(i));

  const updatedStats = computeStats(filteredValues);
  const regression = generateRegression(updatedStats.slope, updatedStats.intercept, filteredValues);

  const getEstevesLabel = (lrr) => {
    if (lrr < -1.0) return ESTEVES_LABELS['Critically Eroded'].class;
    if (lrr < -0.5) return ESTEVES_LABELS['Eroded'].class;
    if (lrr < 0.5) return ESTEVES_LABELS['Stable'].class;
    return ESTEVES_LABELS['Accreted'].class;
  };

  const metadata = {
    [t('forms.transectEvolution.slope')]: formatted(
      updatedStats.slope,
      t('forms.transectEvolution.units.metersPerDay'),
    ),
    [t('forms.transectEvolution.intercept')]: formatted(
      updatedStats.intercept,
      t('forms.transectEvolution.units.meters'),
    ),
    [t('forms.transectEvolution.lrr')]: formatted(updatedStats.lrr, t('forms.transectEvolution.units.mByYr')),
    [t('forms.transectEvolution.r')]: formatted(updatedStats.r),
    [t('forms.transectEvolution.sce')]: formatted(updatedStats.sce, t('forms.transectEvolution.units.meters')),
    [t('forms.transectEvolution.nsm')]: formatted(updatedStats.nsm, t('forms.transectEvolution.units.meters')),
    [t('forms.transectEvolution.epr')]: formatted(updatedStats.epr, t('forms.transectEvolution.units.mByMonth')),
    [t('forms.transectEvolution.eprUnc')]: formatted(updatedStats.eprUnc, t('forms.transectEvolution.units.mByMonth')),
    [t('forms.transectEvolution.lse')]: formatted(updatedStats.lse, t('forms.transectEvolution.units.meters')),
    [t('forms.transectEvolution.lci')]: formatted(updatedStats.lci, t('forms.transectEvolution.units.meters')),
    [t('forms.transectEvolution.label')]: updatedStats.lrr !== undefined ? getEstevesLabel(updatedStats.lrr) : data.label,
  };

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <Typography variant="h6">{t('forms.transectEvolution.title')}</Typography>
        <IconButton onClick={() => setShowHelpDialog(true)} color="primary" size="small">
          <HelpOutlineIcon fontSize="small" />
        </IconButton>
      </div>
      <SimpleInfoDialog
        open={showHelpDialog}
        close={() => setShowHelpDialog(false)}
        title={t('forms.transectEvolution.title')}
        text={t('forms.transectEvolution.TransectEvolutionDialog')}
      />
      <Paper ref={exportRef} elevation={1} className={classes.mainPaper}>
        <div className={classes.chartWrapper}>
          <EvolutionChart
            x={filteredValues.map((el) => fromEpochDays(el[0]).format('YYYY-MM-DD'))}
            y={filteredValues.map((el) => el[1])}
            regression={regression}
            xLabel={t('forms.transectEvolution.labels.x')}
            yLabel={t('forms.transectEvolution.labels.y')}
            trendLabel={t('forms.transectEvolution.labels.trend')}
            yMin={minmaxResults?.minValue ?? 0}
            yMax={minmaxResults?.maxValue ?? 0}
          />
        </div>
        <div className={classes.statsPanel}>
          <Typography variant="subtitle1" className={classes.statsTitle}>
            {t('forms.transectEvolution.statistics')}
          </Typography>
          {Object.entries(metadata).map(([label, value], i) => {
            const showAsterisk = (label === t('forms.transectEvolution.lse') || label === t('forms.transectEvolution.lci') || label === t('forms.transectEvolution.eprUnc')) && value === 'N/A';
            return (
              <div className={classes.statRow} key={i}>
                <Typography variant="body2" className={classes.statLabel}>
                  {label}
                </Typography>
                <Typography variant="body2" className={classes.statValue} title={showAsterisk ? 'Incerteza não estimável com apenas 2 datas. A reta ajusta-se perfeitamente aos pontos, mas não há graus de liberdade suficientes para calcular o erro-padrão.' : ''}>
                  {value}{showAsterisk ? <span style={{ color: 'red', fontWeight: 'bold' }}> *</span> : ''}
                </Typography>
              </div>
            );
          })}
          {filteredValues.length < 3 && (
            <Typography variant="caption" style={{ color: 'red', display: 'block', marginTop: 6 }}>
              * Incerteza não estimável com menos de 3 observações
            </Typography>
          )}
        </div>
      </Paper>
      <Paper elevation={1} className={classes.controlsPaper}>
        <FormControlLabel
          control={
            <Checkbox checked={removeOutliers} onChange={() => setRemoveOutliers(!removeOutliers)} color="secondary" />
          }
          label={t('forms.transectEvolution.outliers')}
        />
        <TextField
          label={t('forms.transectEvolution.deviation')}
          type="number"
          variant="outlined"
          size="small"
          value={outlierMultiplier}
          onChange={(e) => setOutlierMultiplier(parseFloat(e.target.value) || 1)}
          inputProps={{ step: 0.1, min: 0.1 }}
          style={{ width: '100px' }}
        />
        <Button variant="contained" color="primary" onClick={handleExportImage}>
          {t('forms.transectEvolution.exportChart')}
        </Button>
      </Paper>
      <div className={classes.transectSection}>
        <Typography variant="subtitle1" className={classes.transectListTitle}>
          {t('forms.transectEvolution.transectList')}
        </Typography>
        <Paper elevation={1} className={classes.transectList}>
          {sortedValues.map((el, index) => {
            const isOutlier = outlierIndices.includes(index);
            return (
              <FormControlLabel
                key={index}
                className={classes.transectItem}
                control={
                  <Checkbox
                    checked={selectedIndices.includes(index)}
                    onChange={() => handleCheckboxChange(index)}
                    color="primary"
                    disabled={isOutlier}
                  />
                }
                label={`${formatDate(el[0])} — ${el[1]}` + (isOutlier ? ` (${t('Outlier')})` : '')}
              />
            );
          })}
        </Paper>
      </div>
    </div>
  );
};

export default TransectEvolution;
